/**
 * shared/pmove/dtp.ts — dive to prone ("dolphin dive").
 *
 * OWNER: PMOVE agent.  STATUS: ESTIMATE, DISABLED by default (pm.ts PM_DTP_ENABLED).
 *
 * BO2 multiplayer is widely reported to have removed the BO1 dolphin dive [MOVPUB §1.4, ESTIMATE],
 * yet t6mp still registers the dtp_* dvars (dvars_t6mp.json, default `dtp` 1) and the T6 weapons
 * still carry d2p viewmodel anims, and the pmove code tests PMF 0x200000 [PM §1, §3]. The Dtp_*
 * functions are not in the T5 sources we have and were not decompiled, so this is a reconstruction
 * from the dvar names only:
 *   dtp_startup_delay 250 ms (launch window), dtp_max_slide_duration 300 ms, dtp_post_move_pause
 *   100 ms, dtp_exhaustion_window 1500 ms (no re-dive), dtp_min_speed 3.16 (^2 ~ 10 u/s),
 *   dtp_fall_damage_min/max_height 65/200, dtp_new_trajectory_multiplier 2.
 * Turn it on only for experiments.
 */
import { ENTITYNUM_NONE } from '../constants';
import { Button, PEv, PMF, type PlayerState } from '../types';
import { groundSurfaceType } from './footsteps';
import { endSprint } from './sprint';
import { PM_DTP_ENABLED, type Pm } from './pm';

export const DTP_STARTUP_DELAY_MS = 250;
export const DTP_MAX_SLIDE_MS = 300;
export const DTP_POST_MOVE_PAUSE_MS = 100;
export const DTP_EXHAUSTION_WINDOW_MS = 1500;
export const DTP_MIN_SPEED = 3.16;
/** Launch height of the dive arc. ESTIMATE (a third of a jump). */
const DTP_LAUNCH_HEIGHT = 13;

/** ps.dtpState values. */
export const DtpState = { NONE: 0, AIR: 1, SLIDE: 2, PAUSE: 3 } as const;

/** Dtp_CanMove: no input while sliding / in the post-dive pause. */
export function dtpCanMove(ps: PlayerState): boolean {
  return ps.dtpState !== DtpState.SLIDE && ps.dtpState !== DtpState.PAUSE;
}

/** Called from WalkMove next to PM_CheckJump (T5 Dtp_Update). Returns true when a dive launched. */
export function dtpCheckStart(pm: Pm): boolean {
  if (!PM_DTP_ENABLED) return false;
  const ps = pm.ps;
  if (!(ps.pm_flags & PMF.SPRINTING) && !(pm.oldButtons & Button.SPRINT)) return false;
  if (!(pm.cmd.buttons & Button.PRONE) || (pm.oldButtons & Button.PRONE)) return false;
  if (ps.dtpTime && pm.cmd.serverTime - ps.dtpTime < DTP_EXHAUSTION_WINDOW_MS) return false;
  const v = ps.velocity;
  if (v[0] * v[0] + v[1] * v[1] < DTP_MIN_SPEED * DTP_MIN_SPEED) return false;
  endSprint(pm);
  ps.pm_flags |= PMF.DIVE;
  ps.dtpState = DtpState.AIR;
  ps.dtpTime = pm.cmd.serverTime;
  ps.dtpYaw = ps.viewangles[1];
  ps.groundEntityNum = ENTITYNUM_NONE;
  pm.walking = false;
  pm.groundPlane = false;
  v[2] = Math.sqrt(2 * ps.gravity * DTP_LAUNCH_HEIGHT);
  pm.ctx.addEvent(ps, PEv.DIVE_START, 0);
  return true;
}

/** Dtp_CheckForEnd: landing -> slide -> pause -> prone. */
export function dtpUpdate(pm: Pm): void {
  const ps = pm.ps;
  if (!(ps.pm_flags & PMF.DIVE)) return;
  const now = pm.cmd.serverTime;
  if (ps.dtpState === DtpState.AIR) {
    if (ps.groundEntityNum !== ENTITYNUM_NONE && now - ps.dtpTime > 50) {
      ps.dtpState = DtpState.SLIDE;
      ps.dtpTime = now;
      pm.ctx.addEvent(ps, PEv.DIVE_LAND, groundSurfaceType(pm));
    }
  } else if (ps.dtpState === DtpState.SLIDE) {
    if (now - ps.dtpTime >= DTP_MAX_SLIDE_MS) { ps.dtpState = DtpState.PAUSE; ps.dtpTime = now; }
  } else if (ps.dtpState === DtpState.PAUSE) {
    if (now - ps.dtpTime >= DTP_POST_MOVE_PAUSE_MS) {
      ps.dtpState = DtpState.NONE;
      ps.pm_flags &= ~PMF.DIVE;
      ps.pm_flags |= PMF.PRONE;
    }
  }
}

/** Dtp_ReduceFriction: the slide keeps most of the speed. ESTIMATE. */
export function dtpReduceFriction(ps: PlayerState): number {
  return ps.dtpState === DtpState.SLIDE ? 0.25 : 1;
}
